/**
 * Keeps the running step visible as a plan advances.
 *
 * Shared by the chat card and the CoWork panel so the two scroll identically.
 *
 * Scrolls the container directly rather than calling `scrollIntoView`, which
 * walks up every scrollable ancestor: on a long plan that also drags the chat
 * transcript, so the page would jump under the user each time a step ticked
 * over. Here only the list moves.
 *
 * @module components/chat/plan/use-plan-autoscroll
 */

import { useEffect, type RefObject } from "react";

import type { PlanStep } from "@/types/plan";

/**
 * @param ref - The scrollable element wrapping the timeline.
 * @param tasks - Current steps; re-centres whenever their progress changes.
 * @param enabled - False while the list is collapsed, where scrolling is moot.
 */
export function usePlanAutoScroll(
  ref: RefObject<HTMLDivElement | null>,
  tasks: PlanStep[],
  enabled = true,
): void {
  const activeIndex = tasks.findIndex((t) => t.status === "in_progress");
  const completed = tasks.filter((t) => t.status === "completed").length;

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    /**
     * Follow the running step; once every step is done there is none, so follow
     * the end of the list instead — that is where the eye expects to land when
     * a plan finishes.
     */
    const target =
      el.querySelector<HTMLElement>('[data-plan-active="true"]') ??
      (completed > 0 ? (el.lastElementChild?.lastElementChild as HTMLElement | null) : null);
    if (!target) return;

    // Centre the target in the visible band, measured from live rects so it
    // does not depend on which ancestor happens to be positioned.
    const container = el.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    const delta = rect.top - container.top - (el.clientHeight - rect.height) / 2;
    // Sub-pixel deltas fire a pointless smooth-scroll animation on every render.
    if (Math.abs(delta) < 4) return;

    el.scrollTo({ top: el.scrollTop + delta, behavior: "smooth" });
  }, [ref, enabled, activeIndex, completed, tasks.length]);
}
